import deepmerge from 'deepmerge';

const overwriteArray = <T>(_: T[], sourceArray: T[]): T[] => sourceArray;
const concatArray = <T>(destinationArray: T[], sourceArray: T[]): T[] => [
  ...destinationArray,
  ...sourceArray,
];
interface MergeOptions<T> {
  cloneUnlessOtherwiseSpecified(value: T, options: MergeOptions<T>): T;
  // biome-ignore lint/suspicious/noExplicitAny: can receive anything
  isMergeableObject(value: any): value is T;
}
// This is a rename of the api combine strategy
// For each element of an array:
// 1. If it is an object, it will merge it with the existing element at that index,
// if there is one (else put it in whole),
// 2. If it is not an object, it will concat it to the end of the existing array,
// 3. If it is null, it will remove the existing element at that index, if there is one
// 4. If it is undefined, it will leave the existing element at that index as it is
const mergeArray = <T>(target: T[], source: T[], options: MergeOptions<T>): T[] => {
  const destination = target.slice();

  const indexesToRemove: number[] = [];
  for (const [index, item] of source.entries()) {
    if (typeof item === 'undefined') continue;
    if (item === null) indexesToRemove.push(index);
    else if (typeof destination[index] === 'undefined')
      destination[index] = options.cloneUnlessOtherwiseSpecified(item, options);
    else if (options.isMergeableObject(item))
      destination[index] = deepmerge(destination[index], item, options);
    else if (target.indexOf(item) === -1) destination.push(item);
  }

  // This must be sorted descending, otherwise removing earlier indices will change the position
  // in the list for later ones!
  for (const idx of indexesToRemove.reverse()) destination.splice(idx, 1);

  return destination;
};

const arrayStrategyHandler = {
  overwrite: overwriteArray,
  concat: concatArray,
  merge: mergeArray,
};

// TODO: This is basically a copy of api/src/utils/answers.ts,
// we should put this in a shared utility somewhere
export default function mergeObjects<T>(
  updates: T[],
  arrayStrategy: 'overwrite' | 'concat' | 'merge' = 'overwrite',
): T {
  return deepmerge.all(updates, { arrayMerge: arrayStrategyHandler[arrayStrategy] });
}
